// Daten der WEG-Panels (E7): Vorschüsse aller Einheiten der WEG (Rücklage), WEG-Unterlagen
// am Objekt und anstehende Termine (ETV). Geladen erst, wenn ein WEG-Panel offen ist.

import { useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import type { MieterDokument } from '../mieter/mieterDokumente';

export interface WegEeZeile {
  einheit_id: string; eigentuemer_id: string; objekt_id: string; mea: number | string | null;
  hausgeld_vorschuss: number | string | null; ruecklage_vorschuss: number | string | null; vertrag_ab: string | null; vertrag_bis: string | null;
}
export type WegDokument = MieterDokument & { objekt_id: string | null };
export interface WegTermin { id: string; titel: string | null; typ: string | null; start_datum: string; objekt_id: string | null; beschreibung: string | null }

export interface WegDaten { einheitEig: WegEeZeile[]; dokumente: WegDokument[]; termine: WegTermin[] }
export type WegDatenZustand = { status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'ok'; daten: WegDaten };

const DOK = 'id, titel, datei_name, kategorie, datei_typ, datei_url, onedrive_url, gdrive_file_id, created_at, objekt_id';

export function useWegDaten(objektIds: readonly string[], einheitIds: readonly string[], heute: string): WegDatenZustand {
  const [zustand, setZustand] = useState<WegDatenZustand>({ status: 'laedt' });
  const objekte = objektIds.join(',');
  const einheiten = einheitIds.join(',');
  useEffect(() => {
    let aktiv = true;
    const oIds = objekte ? objekte.split(',') : [];
    const eIds = einheiten ? einheiten.split(',') : [];
    if (oIds.length === 0) { setZustand({ status: 'ok', daten: { einheitEig: [], dokumente: [], termine: [] } }); return; }
    setZustand({ status: 'laedt' });
    (async () => {
      const [ee, dok, ter] = await Promise.all([
        eIds.length
          ? supabase.from('einheit_eigentuemer').select('einheit_id, eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss, vertrag_ab, vertrag_bis, einheit:einheit_id(objekt_id)').in('einheit_id', eIds)
          : Promise.resolve({ data: [], error: null }),
        supabase.from('dokumente').select(DOK).in('objekt_id', oIds).eq('is_deleted', false).order('created_at', { ascending: false }).limit(500),
        supabase.from('termine').select('id, titel, typ, start_datum, objekt_id, beschreibung').in('objekt_id', oIds).gte('start_datum', heute).order('start_datum').limit(50),
      ]);
      const fehler = ee.error ?? dok.error ?? ter.error;
      if (fehler) throw new Error(fehler.message);
      const einheitEig = ((ee.data ?? []) as unknown as (Omit<WegEeZeile, 'objekt_id'> & { einheit: { objekt_id: string | null } | null })[])
        .flatMap(r => (r.einheit?.objekt_id ? [{ ...r, objekt_id: r.einheit.objekt_id }] : []));
      if (aktiv) setZustand({ status: 'ok', daten: { einheitEig, dokumente: (dok.data ?? []) as WegDokument[], termine: (ter.data ?? []) as WegTermin[] } });
    })().catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [objekte, einheiten, heute]);
  return zustand;
}
